import { useCallback, useEffect, useLayoutEffect, useMemo, useState, type ReactNode } from 'react';
import { I18nContext, type I18nContextValue } from './i18nContext';
import { mensajesDe } from '@/i18n/locales';
import { buscarIdioma, idiomaPorOmision } from '@/i18n/idiomas';
import { CLAVE_IDIOMA, detectarIdioma, guardarIdioma } from '@/i18n/detectarIdioma';

interface Props {
  children: ReactNode;
  /**
   * Fija el idioma inicial y se salta la detección.
   *
   * No es una comodidad: en jsdom `navigator.language` es `en-US`, así que sin
   * esto **todos** los tests que rendericen a través del proveedor se pondrían
   * en inglés y fallarían contra sus aserciones en español. Los tests pasan
   * `idiomaInicial="es"` para declarar en qué idioma están escritos.
   */
  idiomaInicial?: string;
}

export function I18nProvider({ children, idiomaInicial }: Props) {
  const [idioma, setIdioma] = useState(
    () => (buscarIdioma(idiomaInicial) ?? detectarIdioma()).codigo,
  );

  const cambiarIdioma = useCallback((codigo: string) => {
    if (!buscarIdioma(codigo)) return;
    setIdioma(codigo);
    // Único punto donde se escribe la preferencia. Elegir es el único acto
    // explícito, y por eso es el único que se recuerda: a quien nunca toca el
    // selector se le sigue respetando el idioma de su navegador, incluso si
    // más adelante lo cambia o si el sitio suma idiomas.
    guardarIdioma(codigo);
  }, []);

  // `useLayoutEffect` y no `useEffect`: `lang` tiene que estar puesto antes de
  // que el navegador pinte, para que el lector de pantalla anuncie el primer
  // render en el idioma correcto y la división silábica sea la que toca.
  useLayoutEffect(() => {
    document.documentElement.lang = idioma;
  }, [idioma]);

  // Dos pestañas abiertas deben coincidir. `storage` solo se dispara en las
  // *otras* pestañas, así que no hay eco con la que originó el cambio.
  useEffect(() => {
    function alCambiarAlmacenamiento(evento: StorageEvent) {
      if (evento.key !== CLAVE_IDIOMA) return;
      const nuevo = buscarIdioma(evento.newValue);
      if (nuevo) setIdioma(nuevo.codigo);
    }
    window.addEventListener('storage', alCambiarAlmacenamiento);
    return () => window.removeEventListener('storage', alCambiarAlmacenamiento);
  }, []);

  const value = useMemo<I18nContextValue>(
    () => ({
      idioma,
      t: mensajesDe(idioma),
      locale: (buscarIdioma(idioma) ?? idiomaPorOmision()).locale,
      cambiarIdioma,
    }),
    [idioma, cambiarIdioma],
  );

  return <I18nContext.Provider value={value}>{children}</I18nContext.Provider>;
}
